import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MessageCircle, ExternalLink, Check, SkipForward, Ban, CheckCircle2, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

const VARS = ["[NOME]", "[COGNOME]", "[INTERESSE]", "[COMMERCIALE]"];

export function waNumber(phone) {
  if (!phone) return null;
  const raw = String(phone).trim();
  let d = raw.replace(/\D/g, "");
  if (!d) return null;
  const hadIntl = raw.startsWith("+") || d.startsWith("00");
  if (d.startsWith("00")) d = d.slice(2);
  if (!hadIntl && !d.startsWith("39")) d = "39" + d; // prefisso Italia solo se mancante
  if (d.length < 11 || d.length > 15) return null;
  return d;
}

// Desktop: WhatsApp Web apre direttamente la chat; mobile: wa.me apre l'app
export function waUrl(num, msg) {
  if (!num) return null;
  const mobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
  const t = (msg || "").trim() ? encodeURIComponent(msg) : "";
  if (mobile) return t ? `https://wa.me/${num}?text=${t}` : `https://wa.me/${num}`;
  return `https://web.whatsapp.com/send?phone=${num}${t ? `&text=${t}` : ""}`;
}
export const WA_TARGET = "crm_whatsapp";

export function renderMsg(tpl, lead, userName) {
  const map = {
    "[NOME]": lead.first_name || "",
    "[COGNOME]": lead.last_name || "",
    "[INTERESSE]": (lead.interests || []).join(", "),
    "[COMMERCIALE]": userName || "",
  };
  return String(tpl || "").replace(/\[NOME\]|\[COGNOME\]|\[INTERESSE\]|\[COMMERCIALE\]/g, (m) => map[m] ?? m);
}

export default function WhatsAppQueue({ open, onClose, leads, userName, onLogged }) {
  const [phase, setPhase] = useState("compose");
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const [text, setText] = useState("");
  const [idx, setIdx] = useState(0);
  const [mids, setMids] = useState({});       // lead_id -> wa_message id
  const [openedIds, setOpenedIds] = useState(new Set());
  const [confirmed, setConfirmed] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [invalid, setInvalid] = useState([]);

  useEffect(() => {
    if (!open) return;
    setPhase("compose"); setTplId(""); setText(""); setIdx(0); setMids({});
    setOpenedIds(new Set());
    setConfirmed([]); setSkipped([]); setInvalid([]);
    api.get("/whatsapp-templates", { params: { active_only: true } }).then((r) => setTemplates(r.data)).catch(() => {});
  }, [open]);

  const pickTemplate = (id) => {
    setTplId(id);
    const t = templates.find((x) => x.id === id);
    if (t) setText(t.text);
  };

  const current = leads[idx];
  const previewText = current ? renderMsg(text, current, userName) : "";
  const curNum = current ? waNumber(current.phone) : null;
  const firstPreview = leads[0] ? renderMsg(text, leads[0], userName) : "";
  const total = leads.length;

  const patch = (leadId, status) => {
    const mid = mids[leadId];
    if (mid) return api.patch(`/whatsapp/messages/${mid}`, { status }).catch(() => {});
  };

  const startQueue = async () => {
    const t = templates.find((x) => x.id === tplId);
    try {
      const r = await api.post("/whatsapp/messages", {
        lead_ids: leads.map((l) => l.id), message: text,
        template_id: tplId || null, template_name: t ? t.name : null,
      });
      const map = {};
      (r.data || []).forEach((rec) => { map[rec.lead_id] = rec.id; });
      setMids(map);
    } catch { /* non blocca il flusso assistito */ }
    setIdx(0); setPhase("queue");
  };

  const advance = () => {
    if (idx + 1 >= leads.length) setPhase("summary");
    else setIdx(idx + 1);
  };

  const urlFor = (lead) => (lead ? waUrl(waNumber(lead.phone), renderMsg(text, lead, userName)) : null);
  // L'apertura avviene tramite un vero link <a target> (mai bloccato dal browser):
  // qui si registra solo lo stato "aperto", NON l'invio.
  const markOpened = (lead) => {
    setOpenedIds((s) => new Set(s).add(lead.id));
    patch(lead.id, "opened");
  };

  // "Inviato, prossimo": conferma manuale del corrente; se il successivo ha un numero valido, il link ne apre subito la chat
  const next = leads[idx + 1];
  const nextUrl = urlFor(next);
  const confirmNext = () => {
    if (!current) return;
    const cur = current;
    patch(cur.id, "confirmed");
    // Aggiornamenti di stato differiti: il link deve navigare verso l'href attuale (lead successivo) prima del re-render
    setTimeout(() => {
      if (next && nextUrl) markOpened(next);
      setConfirmed((c) => [...c, cur]);
      onLogged && onLogged();
      advance();
    }, 0);
  };

  const skip = () => { patch(current.id, "cancelled"); setSkipped((s) => [...s, current]); advance(); };
  const markInvalidNext = () => { patch(current.id, "cancelled"); setInvalid((s) => [...s, current]); advance(); };
  const curOpened = current ? openedIds.has(current.id) : false;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="wa-queue-dialog">
        {phase === "compose" && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-emerald-500" /> Coda WhatsApp
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-sm">
                <b data-testid="wa-recipient-count">{total}</b> destinatari selezionati
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Template salvato</Label>
                <Select value={tplId} onValueChange={pickTemplate}>
                  <SelectTrigger data-testid="wa-template-select"><SelectValue placeholder="Nessuno (scrivi sotto)" /></SelectTrigger>
                  <SelectContent>
                    {templates.length === 0 && <div className="px-2 py-3 text-xs text-muted-foreground">Nessun template attivo</div>}
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}{t.interest ? ` · ${t.interest}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Messaggio</Label>
                <Textarea data-testid="wa-message-text" rows={5} value={text} onChange={(e) => setText(e.target.value)}
                  placeholder="Ciao [NOME], sono [COMMERCIALE]…" />
                <div className="flex flex-wrap gap-1 pt-1">
                  {VARS.map((v) => (
                    <button key={v} type="button" onClick={() => setText((x) => x + v)}
                      className="text-[11px] px-2 py-0.5 rounded-full border bg-muted hover:bg-accent">{v}</button>
                  ))}
                </div>
              </div>
              {firstPreview && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Anteprima (primo lead)</Label>
                  <div data-testid="wa-preview" className="rounded-md border bg-card p-3 text-sm whitespace-pre-wrap">{firstPreview}</div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              <Button data-testid="wa-start-queue" disabled={!text.trim()} onClick={startQueue}>
                Avvia coda ({total})
              </Button>
            </DialogFooter>
          </>
        )}

        {phase === "queue" && current && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center justify-between">
                <span className="flex items-center gap-2"><MessageCircle className="w-5 h-5 text-emerald-500" /> WhatsApp – {idx + 1} di {total}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <div className="font-semibold text-lg" data-testid="wa-current-name">{current.first_name} {current.last_name}</div>
                {curNum ? (
                  <div className="text-sm text-muted-foreground" data-testid="wa-current-phone">{current.phone} <span className="opacity-60">(+{curNum})</span></div>
                ) : (
                  <div className="text-sm text-destructive font-medium" data-testid="wa-invalid-number">Numero mancante/non valido</div>
                )}
              </div>
              <div className="rounded-md border bg-card p-3 text-sm whitespace-pre-wrap" data-testid="wa-current-preview">{previewText}</div>
              {curOpened && curNum && (
                <a href={urlFor(current)} target={WA_TARGET} data-testid="wa-reopen-link"
                  className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-400 underline">
                  <AlertTriangle className="w-4 h-4" /> La chat non si è aperta? Clicca qui per riaprirla
                </a>
              )}
              <p className="text-xs text-muted-foreground">
                1) "Invia su WhatsApp" apre la chat col messaggio pronto · 2) invia da WhatsApp · 3) "Inviato, prossimo" registra la conferma manuale e apre subito il contatto successivo. La conferma NON verifica la consegna.
              </p>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              {curNum ? (
                <>
                  {!curOpened ? (
                    <Button key="btn-send" asChild className="gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white">
                      <a key="wa-send" href={urlFor(current)} target={WA_TARGET} onClick={() => { const cur = current; setTimeout(() => markOpened(cur), 0); }} data-testid="wa-send">
                        <ExternalLink className="w-4 h-4" /> Invia su WhatsApp
                      </a>
                    </Button>
                  ) : nextUrl ? (
                    <Button key="btn-next" asChild className="gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white">
                      <a key="wa-next" href={nextUrl} target={WA_TARGET} onClick={confirmNext} data-testid="wa-confirm-next">
                        <Check className="w-4 h-4" /> Inviato, prossimo
                      </a>
                    </Button>
                  ) : (
                    <Button key="btn-end" className="gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white" onClick={confirmNext} data-testid="wa-confirm-next">
                      <Check className="w-4 h-4" /> {idx + 1 >= total ? "Inviato, termina" : "Inviato, prossimo"}
                    </Button>
                  )}
                  <Button variant="ghost" className="gap-1.5" onClick={skip} data-testid="wa-skip"><SkipForward className="w-4 h-4" /> Salta</Button>
                </>
              ) : (
                <Button className="gap-1.5" onClick={markInvalidNext} data-testid="wa-invalid-next"><SkipForward className="w-4 h-4" /> Salta (numero non valido)</Button>
              )}
              <Button variant="ghost" className="gap-1.5 text-destructive sm:ml-auto" onClick={() => setPhase("summary")} data-testid="wa-stop"><Ban className="w-4 h-4" /> Interrompi coda</Button>
            </DialogFooter>
          </>
        )}

        {phase === "summary" && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500" /> Coda completata</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 text-sm" data-testid="wa-summary">
              <Row label="Lead selezionati" value={total} />
              <Row label="Confermati (manuale, non verificato)" value={confirmed.length} className="text-emerald-600" />
              <Row label="Saltati / annullati" value={skipped.length} />
              <Row label="Numeri non validi" value={invalid.length} className="text-destructive" />
            </div>
            <DialogFooter>
              <Button onClick={onClose} data-testid="wa-close">Chiudi</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, className }) {
  return (
    <div className="flex items-center justify-between rounded-md border bg-card px-3 py-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("font-heading font-black tabular-nums text-lg", className)}>{value}</span>
    </div>
  );
}
